import { Badge, Button, Card, Group, MultiSelect, Select, Stack, Text } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import {
  type LlmModel,
  useLlmModels,
  useLlmTasks,
} from 'src/secret-menu/llm-dispatcher/hooks/useLlmCatalogue';

import { type ReportError, useCreateArenaRun } from '../hooks/useModelArena';
import { countLabel } from '../runLabels';

import { sentenceCase } from './metricFormat';

import { groupCandidates, qualified } from './candidateOptions';

const ALL = 'all';

/** What the API accepts. Each candidate is measured over every document in turn, so the bound is
 * about time and spend rather than anything technical. */
const MOST_CANDIDATES = 12;

/** All of them, then each smaller count. Bounded by what the set actually holds, so a run cannot be
 * asked for eight documents out of seven — and left as just "all" when W&B could not say. */
const documentOptions = (documents: number | null) => [
  { value: ALL, label: documents === null ? 'All of them' : `All ${documents}` },
  ...Array.from({ length: documents ?? 0 }, (_, index) => ({
    value: String(index + 1),
    label: String(index + 1),
  })),
];

export function NewRunForm({
  taskKey,
  scoringModels,
  documents,
  onQueued,
  reportError,
}: {
  taskKey: string;
  scoringModels: string[];
  documents: number | null;
  onQueued: () => void;
  reportError: ReportError;
}) {
  const { models } = useLlmModels();
  const { tasks: dispatcherTasks } = useLlmTasks();
  const { createRun, isQueueing } = useCreateArenaRun(reportError);
  const [selectedModelIds, setSelectedModelIds] = useState<string[]>([]);
  const [scoring, setScoring] = useState<Record<string, string>>({});
  const [fixtureLimit, setFixtureLimit] = useState<string>(ALL);
  const [notBefore, setNotBefore] = useState<string | null>(null);

  const task = dispatcherTasks?.find(candidate => candidate.key === taskKey);
  const catalogue = models ?? [];
  const pooledIds = new Set(task?.pool.map(entry => entry.model.id) ?? []);
  const candidateCount = countLabel(selectedModelIds.length, 'candidate');
  const everyScoringModelPicked = scoringModels.every(name => scoring[name]);

  const submit = async () => {
    const queued = await createRun({
      taskKey,
      candidates: selectedModelIds.map(modelId => ({ modelId })),
      scoring,
      fixtureLimit: fixtureLimit === ALL ? undefined : Number(fixtureLimit),
      notBefore: notBefore ? new Date(notBefore).toISOString() : undefined,
    });
    if (!queued) return;

    const scoredBy = Object.values(scoring).join(', ');

    // The selection is deliberately kept: queueing the same candidates scored differently is a
    // likely next move, and an emptied form reads as one that never submitted.
    notifications.show({
      title: 'Run queued',
      message: scoredBy === '' ? candidateCount : `${candidateCount}, scored by ${scoredBy}`,
      color: 'teal',
    });
    onQueued();
  };

  return (
    <Card withBorder p="md" radius="sm">
      <Stack gap="sm">
        <MultiSelect
          label="Candidates"
          description="Any model in the catalogue can be compared, not only the ones this task already uses"
          placeholder={catalogue.length ? 'Pick models to compare' : 'No models in the catalogue'}
          data={groupCandidates(catalogue, task)}
          value={selectedModelIds}
          onChange={setSelectedModelIds}
          renderOption={({ option }) => (
            <Group gap="xs" justify="space-between" w="100%" wrap="nowrap">
              <Text size="sm">{option.label}</Text>
              {pooledIds.has(option.value) && (
                <Badge size="xs" color="teal" variant="light">
                  in pool
                </Badge>
              )}
            </Group>
          )}
          maxValues={MOST_CANDIDATES}
          searchable
          clearable
        />
        <SelectionSummary
          models={catalogue}
          selectedModelIds={selectedModelIds}
          pooledIds={pooledIds}
        />
        {/* One field per model the task says it is scored by — none for a task scored without one.
            Rendering what the task declares is what keeps adding a task out of this file. */}
        {scoringModels.map(name => (
          <Select
            key={name}
            label={sentenceCase(name)}
            description="What scores the run against gold. It shapes every number, so two runs scored differently are not comparable"
            placeholder="Pick the model to score with"
            data={catalogue.map(qualified).sort()}
            value={scoring[name] ?? null}
            onChange={picked => setScoring(chosen => ({ ...chosen, [name]: picked ?? '' }))}
            searchable
          />
        ))}
        <Select
          label="Documents"
          description="How many of the fixture set to measure on. Fewer is a quicker, cheaper trial — but a leaderboard from a subset is not comparable with a full one"
          data={documentOptions(documents)}
          value={fixtureLimit}
          onChange={value => setFixtureLimit(value ?? ALL)}
          allowDeselect={false}
        />
        <DateTimePicker
          label="Start"
          description="Leave empty to start as soon as a worker is free"
          placeholder="As soon as possible"
          value={notBefore}
          onChange={setNotBefore}
          clearable
        />
        <Group>
          <Button
            onClick={submit}
            loading={isQueueing}
            disabled={selectedModelIds.length === 0 || !everyScoringModelPicked}
          >
            Queue run
          </Button>
          {selectedModelIds.length > 0 && (
            <Text size="xs" c="dimmed">
              {candidateCount} · each runs the full pipeline over every fixture
            </Text>
          )}
        </Group>
      </Stack>
    </Card>
  );
}

/** The picked models as pills, so which are already in service stays visible after the dropdown closes. */
function SelectionSummary({
  models,
  selectedModelIds,
  pooledIds,
}: {
  models: LlmModel[];
  selectedModelIds: string[];
  pooledIds: Set<string>;
}) {
  if (selectedModelIds.length === 0) return null;

  return (
    <Group gap="xs">
      {models
        .filter(model => selectedModelIds.includes(model.id))
        .map(model => (
          <Badge
            key={model.id}
            size="sm"
            variant="light"
            color={pooledIds.has(model.id) ? 'teal' : 'grape'}
          >
            {qualified(model)}
          </Badge>
        ))}
    </Group>
  );
}
